import { useCallback, useEffect, useState } from 'react'

import { asFailure, type Failure } from './request'

/**
 * Something the frontend asked the service for, in the three states it can be in.
 *
 * There are only ever these three, everywhere, so that waiting, having, and having failed look and
 * sound the same in every area. See `ui/Status.tsx` for what each one looks like.
 */
export type Load<T> =
  | { readonly status: 'waiting' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'failed'; readonly failure: Failure }

export interface Loaded<T> {
  readonly load: Load<T>
  /** Asks again. Safe to offer for any failure; harmless for one that will fail the same way. */
  readonly retry: () => void
}

/**
 * Runs one request and follows it through its three states.
 *
 * `loader` has to be stable — wrap it in `useCallback` at the call site — because a new function
 * on every render would ask again on every render. An abort from the returned signal means the
 * caller went away, and is never reported as a failure.
 */
export function useLoad<T>(loader: (signal: AbortSignal) => Promise<T>): Loaded<T> {
  const [state, setState] = useState<Load<T>>({ status: 'waiting' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()

    loader(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) {
          setState({ status: 'ready', value })
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setState({ status: 'failed', failure: asFailure(error) })
        }
      })

    return () => {
      controller.abort()
    }
  }, [loader, attempt])

  /**
   * Asks again — and **keeps what it already has while it does**.
   *
   * That is not a nicety. Asking again is what an area does after it changed something, and a
   * moment of `waiting` in between empties the screen: a list becomes empty, whatever was open
   * inside it is no longer found, and the pane holding it is taken out of the document and built
   * again. In an editor that means the text is re-read and the cursor goes back to the start of it
   * — while somebody is typing, because the automatic save is what asked.
   *
   * So a value that is already there stays on the screen until a newer one replaces it. What is
   * shown is then the truth as of a moment ago, which is worth incomparably more than nothing at
   * all. There is nothing to keep after a failure, so that case still goes to `waiting`.
   */
  const retry = useCallback(() => {
    setState((current) => (current.status === 'ready' ? current : { status: 'waiting' }))
    setAttempt((previous) => previous + 1)
  }, [])

  return { load: state, retry }
}
